<template>
  <div class="credits-container">
    <div class="top-wrapper">
      <h3 class="credit-title">billing cycle usage</h3>
      <sb-tab
          v-if="!isLoading && creditHistory['organization']"
          :tabs="tabs"
          :selected.sync="selectedTab"
          @click.native="changeTab()"
      ></sb-tab>
    </div>
    <div class="error-message">{{ pageError }}</div>
    <loading-icon v-if="isLoading"></loading-icon>
    <sb-table
      v-else
      class="credits-table history"
      ref="credit-history"
      :class="`${selectedTab.key}_view`"
      :columns="creditHistory[selectedTab.key].columns"
      :rows="creditHistory[selectedTab.key].rows"
    >
        <template #cell="{ row, column }">
            <div v-if="column.key === 'brand'">
                <div v-for="advertiser in row.advertiser_data" :key="advertiser.name" class="advertiser_data">
                    <div>{{advertiser.name}}</div>
                    <div>{{ numberWithCommas(advertiser.text_credits)}}</div>
                </div>
            </div>
            <div
            :class="`${isCurrentCycle(row['start_date'], row['end_date'])}`"
            >
                {{ row[column.key] }}
            </div>
        </template>
    </sb-table>
    <pagination
        v-if="!isLoading && totalPageCount && totalPageCount > 1"
        :currentPage="currentPage"
        :totalPageCount="totalPageCount"
        showArrows
        @changePage="changePage">
    </pagination>
  </div>
</template>

<script>
import LoadingIcon from "../Shared/LoadingIcon";
import moment from 'moment';
import Pagination from '../Shared/Pagination';
import { brandsAPI } from "../../services/api/brands";
import { NumberFormatters } from '../mixins/NumberFormatters.js';

const orgColumns = [
  { label: "date", key: "date" },
  { label: "type", key: "type" },
  { label: "brand", key: "brand" },
  { label: "sent", key: "sent" }
];
const adColumns = [
  { label: "date", key: "date" },
  { label: "type", key: "type" },
  { label: "sent", key: "sent" }
];

export default {
  name: "Credits",

  mixins: [NumberFormatters],

  components: {
    LoadingIcon,
    Pagination
  },

  props: {
    currentAdvertiser: Object,
  },

  data() {
    return {
      creditHistory: {
          organization: {
            columns: orgColumns,
            rows: [],
            pagination: {}
          },
          advertiser: {
            columns: adColumns,
            rows: [],
            pagination: {}
          }
      },
      isLoading: true,
      currentPage: 1,
      pageError: null,
      selectedTab: null,
      tabs: [
          { label: "organization", key: "organization" },
          { label: "advertiser", key: "advertiser" }
        ],
      totalPageCount: 1
    };
  },

  methods: {
    async changePage(page) {
      this.currentPage = page
      this.isLoading = true;
      try {
        await this.fetchBrandCreditUsage(this.selectedTab.key)
      } catch (error) {
        this.pageError = error
      }
      this.isLoading = false;
    },
    changeTab() {
        this.currentPage = 1
        this.getAllCreditUsage()
    },
    configureTotalPages(pagination) {
      const totalPageCount = Math.ceil(pagination.total_elements / pagination.per_page)
      this.totalPageCount = totalPageCount !== this.totalPageCount ? totalPageCount : this.totalPageCount
    },
    formatDateRange(start, end){
      return moment(start).utc().format('MM/DD/YYYY') + ' - ' + moment(end).utc().format('MM/DD/YYYY')
    },
    generateCreditUsageRows(data){
      return data.map(val => {
        'text_credits' in val ? val.sent = this.numberWithCommas(val.text_credits) : val.sent = '';
        val.date = this.formatDateRange(val.start_date, val.end_date)
        val.type = 'messages'
        return val
      })
    },
    async getAllCreditUsage() {
      this.isLoading = true;
      try {
        await this.fetchBrandCreditUsage('organization');
        await this.fetchBrandCreditUsage('advertiser');
      } catch (error) {
          this.pageError = error
      }
      this.configureTotalPages(this.creditHistory[this.selectedTab.key].pagination)
      this.isLoading = false
    },
    fetchBrandCreditUsage(type) {
        return new Promise(async (resolve, reject) => {
            try {
                const capitalizedType = type.charAt(0).toUpperCase() + type.slice(1);
                const creditUsage = await brandsAPI[`get${capitalizedType}CreditUsage`](this.currentAdvertiser.id, this.currentPage)
                this.creditHistory[type.toLowerCase()].rows = this.generateCreditUsageRows(creditUsage.credit_usage);
                this.creditHistory[type.toLowerCase()].pagination = creditUsage.pagination
                resolve()
            } catch (error) {
                const responseCode = error.response?.status;
                if (type === 'organization' && responseCode === 401) {
                    this.creditHistory.organization = null
                    this.selectedTab = this.tabs[1]
                    resolve();
                } else {
                    reject(error)
                }
            }
        })
    },
    isCurrentCycle(startTime, endTime) {
        const currentTime = moment();
        return currentTime.isBetween(moment(startTime), moment(endTime)) ? 'current' : '';
    }
  },

  created() {
    this.getAllCreditUsage();
    this.selectedTab = this.tabs[0]
  },

  watch: {
    selectedTab: function() {
      this.currentPage = 1
      this.getAllCreditUsage()
    }
  }
};
</script>

<style lang="sass">
@import '../../assets/sass/colors'
.credits-container
  padding-top: 2rem
  text-align: center
  margin-bottom: 200px

  .top-wrapper
    position: relative
    @media only screen and (max-width: 610px)
      display: flex
      flex-direction: column
      margin-bottom: 3.5rem
      position: unset
    .credit-title
      margin-bottom: 3.5rem
      @media only screen and (max-width: 800px)
        margin-bottom: 2rem
    .sb-tabs
      width: max-content
      margin: 0 auto
      position: absolute
      top: 0
      right: 40px
      @media only screen and (max-width: 800px)
        margin-bottom: 2rem
        position: unset

      .item
        width: 120px
        &.is-active
          background-color: $active-primary-background
          span
            background-color: unset !important
        &:hover
          &:not(.is-active)
            background-color: $active-primary-background

  .credits-table
    width: 50%
    margin: 0 auto
    font-size: 1.15rem
    text-align: right

    &.organization_view
      .grid-content
        align-items: flex-start
        grid-template-columns: 1fr 1fr 2fr !important
        border-bottom: 1px solid #eee

        &:last-child
          border-bottom: none

        .advertiser_data
          display: grid
          grid-template-columns: repeat(2, 1fr)
          justify-items: right
          margin-bottom: 20px

          div:first-child
            padding-left: 50px
            word-break: break-word

    .grid-content
      margin-top: 25px

    .grid-header, .grid-content
      div:first-child
        text-align: left

.current
  @at-root .grid-content#{&}
  margin-bottom: 50px

</style>
